// ── CASH DRAWER HEALTH (change alerts + drop suggestions) ────────────────────
// Pure integer-cents / count math. No floats for money.

import {
  centsToMoneyString,
  parseCountQty,
  totalDenominationsCents,
  type CashDenominationConfig,
  type CashDenomUnit,
  type DenominationCounts,
} from './cash-denominations.js';

export type DrawerHealthSeverity = 'ok' | 'warning' | 'critical';

export type ChangeAlertSettings = {
  changeAlertsEnabled: boolean;
  /** Minimum COUNT per denom key. 0 or absent = no alert for that key. */
  changeMinimums: Record<string, number>;
  /** Dollar value in $50+ bills that suggests a drop (as money string/number). */
  largeBillThreshold: number | string;
  /** Total drawer value threshold for drop suggestion. */
  dropThreshold: number | string;
};

export type LowChangeItem = {
  denomination: string;
  label: string;
  counted: number;
  minimum: number;
  shortBy: number;
  /** True when counted effective qty is literally 0 and a minimum was set. */
  isOut: boolean;
  valueCents: number;
};

export type BankOrderLine = {
  denomination: string;
  label: string;
  kind: 'roll' | 'strap';
  packQuantity: number;
  unitsPerPack: number;
  totalValueCents: number;
  description: string;
};

export type DrawerHealthResult = {
  lowChange: LowChangeItem[];
  largeBillTotalCents: number;
  largeBillThresholdCents: number;
  exceedsLargeBillThreshold: boolean;
  drawerTotalCents: number;
  dropThresholdCents: number;
  exceedsDropThreshold: boolean;
  severity: DrawerHealthSeverity;
  suggestedBankOrder: BankOrderLine[];
};

/** Bills $50 and above (valueCents ≥ 5000). */
export const LARGE_BILL_MIN_CENTS = 5000;

/** Suggested starter minimums for USD stores that enable alerts. */
export const DEFAULT_CHANGE_MINIMUMS: Record<string, number> = {
  '2000': 5,
  '1000': 5,
  '500': 10,
  '100': 25,
  quarter: 40,
  dime: 50,
  nickel: 40,
  penny: 50,
};

/** Bill strap sizes (face-count of bills, dollar total as cents). */
export const BILL_STRAP_PACKS: Record<string, { unitsPerStrap: number; valueCents: number }> = {
  '100': { unitsPerStrap: 25, valueCents: 2500 }, // $25
  '500': { unitsPerStrap: 20, valueCents: 10000 }, // $100
  '1000': { unitsPerStrap: 25, valueCents: 25000 }, // $250
  '2000': { unitsPerStrap: 25, valueCents: 50000 }, // $500
};

function moneyToCents(value: number | string | null | undefined): number {
  return Math.round(Number(value ?? 0) * 100);
}

function unitByKey(config: CashDenominationConfig, key: string): CashDenomUnit | null {
  return (
    config.bills.find((b) => b.key === key) ??
    config.coins.find((c) => c.key === key) ??
    null
  );
}

/**
 * Effective piece count for a denomination, expanding coin rolls into loose units.
 */
export function effectiveDenomCount(
  counts: DenominationCounts | null | undefined,
  unit: CashDenomUnit,
  config: CashDenominationConfig,
  trackCoinRolls: boolean,
): number {
  let qty = parseCountQty(counts?.[unit.key]);
  if (trackCoinRolls && counts?.rolls && config.coins.some((c) => c.key === unit.key)) {
    const rolls = parseCountQty(counts.rolls[unit.key]);
    if (rolls > 0 && unit.valueCents > 0) {
      const rollCents = Math.round(Number(config.rollValues[unit.key] ?? 0));
      const perRoll = Math.floor(rollCents / unit.valueCents);
      if (perRoll > 0) qty += rolls * perRoll;
    }
  }
  return Math.max(0, qty);
}

export function suggestBankOrder(
  lowChange: LowChangeItem[],
  config: CashDenominationConfig,
): BankOrderLine[] {
  const lines: BankOrderLine[] = [];

  for (const item of lowChange) {
    if (item.shortBy <= 0) continue;
    const unit = unitByKey(config, item.denomination);
    if (!unit || unit.valueCents <= 0) continue;

    const isCoin = config.coins.some((c) => c.key === unit.key);
    if (isCoin) {
      const rollCents = Math.round(Number(config.rollValues[unit.key] ?? 0));
      const unitsPerPack = rollCents > 0 ? Math.floor(rollCents / unit.valueCents) : 0;
      if (unitsPerPack <= 0) continue;
      const packQuantity = Math.ceil(item.shortBy / unitsPerPack);
      const totalValueCents = packQuantity * rollCents;
      lines.push({
        denomination: unit.key,
        label: unit.label,
        kind: 'roll',
        packQuantity,
        unitsPerPack,
        totalValueCents,
        description: `${packQuantity} roll${packQuantity === 1 ? '' : 's'} (${centsToMoneyString(totalValueCents)})`,
      });
      continue;
    }

    const strap = BILL_STRAP_PACKS[unit.key];
    if (strap) {
      const packQuantity = Math.ceil(item.shortBy / strap.unitsPerStrap);
      const totalValueCents = packQuantity * strap.valueCents;
      lines.push({
        denomination: unit.key,
        label: unit.label,
        kind: 'strap',
        packQuantity,
        unitsPerPack: strap.unitsPerStrap,
        totalValueCents,
        description: `${packQuantity} strap${packQuantity === 1 ? '' : 's'} (${centsToMoneyString(totalValueCents)})`,
      });
      continue;
    }

    // No pack definition — order exact short count
    const totalValueCents = item.shortBy * unit.valueCents;
    lines.push({
      denomination: unit.key,
      label: unit.label,
      kind: 'strap',
      packQuantity: item.shortBy,
      unitsPerPack: 1,
      totalValueCents,
      description: `${item.shortBy} × ${unit.label} (${centsToMoneyString(totalValueCents)})`,
    });
  }

  return lines;
}

/**
 * Shared evaluator for open/close (and live UI).
 * Advisory only — never blocks session lifecycle.
 */
export function evaluateDrawerHealth(input: {
  denominations: DenominationCounts | null | undefined;
  config: CashDenominationConfig;
  trackCoinRolls?: boolean;
  settings: ChangeAlertSettings;
  /** Optional override when count method is manual_total */
  drawerTotalCentsOverride?: number | null;
}): DrawerHealthResult {
  const trackCoinRolls = input.trackCoinRolls ?? false;
  const empty: DrawerHealthResult = {
    lowChange: [],
    largeBillTotalCents: 0,
    largeBillThresholdCents: moneyToCents(input.settings.largeBillThreshold),
    exceedsLargeBillThreshold: false,
    drawerTotalCents: 0,
    dropThresholdCents: moneyToCents(input.settings.dropThreshold),
    exceedsDropThreshold: false,
    severity: 'ok',
    suggestedBankOrder: [],
  };

  if (!input.settings.changeAlertsEnabled) {
    return empty;
  }

  const mins = input.settings.changeMinimums ?? {};
  const drawerTotalCents =
    input.drawerTotalCentsOverride != null
      ? Math.max(0, Math.round(input.drawerTotalCentsOverride))
      : totalDenominationsCents(input.denominations, input.config, trackCoinRolls);

  const lowChange: LowChangeItem[] = [];
  let hasZero = false;

  for (const [key, rawMin] of Object.entries(mins)) {
    const minimum = Math.trunc(Number(rawMin));
    if (!Number.isFinite(minimum) || minimum <= 0) continue;
    const unit = unitByKey(input.config, key);
    if (!unit || !unit.enabled) continue;

    const counted = effectiveDenomCount(
      input.denominations,
      unit,
      input.config,
      trackCoinRolls,
    );
    if (counted >= minimum) continue;
    const shortBy = minimum - counted;
    const isOut = counted === 0;
    if (isOut) hasZero = true;
    lowChange.push({
      denomination: key,
      label: unit.label,
      counted,
      minimum,
      shortBy,
      isOut,
      valueCents: unit.valueCents,
    });
  }

  // Stable order: out first, then by shortBy desc
  lowChange.sort((a, b) => {
    if (a.isOut !== b.isOut) return a.isOut ? -1 : 1;
    return b.shortBy - a.shortBy;
  });

  let largeBillTotalCents = 0;
  for (const bill of input.config.bills) {
    if (!bill.enabled || bill.valueCents < LARGE_BILL_MIN_CENTS) continue;
    const qty = effectiveDenomCount(
      input.denominations,
      bill,
      input.config,
      trackCoinRolls,
    );
    largeBillTotalCents += qty * bill.valueCents;
  }

  const largeBillThresholdCents = moneyToCents(input.settings.largeBillThreshold);
  const dropThresholdCents = moneyToCents(input.settings.dropThreshold);
  const exceedsLargeBillThreshold = largeBillTotalCents > largeBillThresholdCents;
  const exceedsDropThreshold = drawerTotalCents > dropThresholdCents;
  const exceedsCriticalDrop =
    dropThresholdCents > 0 && drawerTotalCents > Math.floor((dropThresholdCents * 150) / 100);

  let severity: DrawerHealthSeverity = 'ok';
  if (
    lowChange.length > 0 ||
    exceedsLargeBillThreshold ||
    exceedsDropThreshold
  ) {
    severity = 'warning';
  }
  if (hasZero || exceedsCriticalDrop) {
    severity = 'critical';
  }

  return {
    lowChange,
    largeBillTotalCents,
    largeBillThresholdCents,
    exceedsLargeBillThreshold,
    drawerTotalCents,
    dropThresholdCents,
    exceedsDropThreshold,
    severity,
    suggestedBankOrder: suggestBankOrder(lowChange, input.config),
  };
}

/**
 * Suggest minimums from average daily/session consumption.
 * suggested = max( current floor-ish, ceil(avg * 1.5) rounded to a nicer step ).
 */
export function suggestMinimumFromAverageUsage(avgConsumedPerSession: number): number {
  if (!Number.isFinite(avgConsumedPerSession) || avgConsumedPerSession <= 0) return 0;
  const boosted = Math.ceil(avgConsumedPerSession * 1.5);
  // Round to a friendly counter step (5 for < 50, 10 for larger)
  const step = boosted < 50 ? 5 : 10;
  return Math.max(step, Math.ceil(boosted / step) * step);
}
